import React, { useState } from "react";

import usePatchDisclosures from "@fartherfinance/frontend/api/Document/hooks/usePatchDisclosures";
import { ClientId } from "@fartherfinance/frontend/api/Types";

import Button from "@src/multiCustodian/components/MUI/Button/Button";
import useApplicationMode from "@src/multiCustodian/hooks/useApplicationMode";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import { captureException } from "@src/multiCustodian/services/tracking";
import LogoLoadingStill from "@src/sharedComponents/LogoLoadingStill/LogoLoadingStill";
import Modal from "@src/sharedComponents/Modal/Modal";
import Text from "@src/sharedComponents/Typography/Text";

import Disclosure, { DisclosureProps } from "./Disclosure";

import styles from "./DisclosuresModal.module.css";

const disclosures: DisclosureProps[] = [
  {
    type: "privacyPolicy",
    header: "Privacy Policy",
    description:
      "This document details the terms and conditions of your relationship with Farther as your Investment Advisor.",
  },
  {
    type: "adv",
    header: "Form ADV",
    description:
      "This document provides information about the qualifications and business practices of Farther Finance Advisors, LLC.",
  },
  {
    type: "crs",
    header: "Form CRS",
    description:
      "This document details our investment advisory and financial planning services.",
  },
];

interface DisclosuresModalProps {
  clientId: ClientId | null;
  isModalOpen: boolean;
  onClose: () => void;
}

const DisclosuresModal: React.FC<DisclosuresModalProps> = ({
  clientId,
  isModalOpen,
  onClose,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const { isImpersonationMode } = useApplicationMode();
  const auth = useRequestAuth();
  const callPatchDisclosures = usePatchDisclosures(auth);
  const statusNotification = useStatusNotification();

  const handleAcceptDisclosures = async (): Promise<void> => {
    if (isImpersonationMode || !clientId) {
      return;
    }

    try {
      setIsLoading(true);

      await callPatchDisclosures(clientId);
      onClose();
      statusNotification(
        "Farther Disclosures accepted successfully.",
        "Success"
      );
    } catch (error) {
      statusNotification(
        "Something went wrong, please try again later.",
        "Error"
      );
      captureException(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading && <LogoLoadingStill onTop />}

      <Modal
        className={styles.modal}
        modalStyle={{ width: "564px", backgroundColor: "var(--surface)" }}
        closeModal={onClose}
        isOpen={isModalOpen}
      >
        <div className={styles.content}>
          <Text variant="title1">Farther Disclosures</Text>
          <Text
            className={styles.description}
            variant="body"
            color="textSubtle"
          >
            Here is an overview of the Farther Agreements. Review and accept the
             agreements.
          </Text>

          {disclosures.map((disclosure) => (
            <Disclosure
              key={disclosure.type}
              type={disclosure.type}
              header={disclosure.header}
              description={disclosure.description}
            />
          ))}
        </div>

        <div className={styles.footer}>
          <Button
            variant="outlined"
            buttonType="primary"
            text="Close"
            onClick={onClose}
          />

          <Button
            style={{
              cursor: isImpersonationMode ? "not-allowed" : "pointer",
            }}
            disabled={isImpersonationMode}
            variant="contained"
            buttonType="primary"
            text="Accept"
            onClick={handleAcceptDisclosures}
          />
        </div>
      </Modal>
    </>
  );
};

export default DisclosuresModal;
